import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  Wine, Beer, Coffee, GlassWater, Plus, Edit2, Trash2, Save, X, Search,
  Upload, Image, Loader2, RefreshCw, ChevronDown, AlertCircle, Check, Martini
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Category definitions
const CATEGORIES = {
  soft: { label: 'Soft Drinks', icon: GlassWater, color: 'blue' },
  beer: { label: 'Beers', icon: Beer, color: 'amber' },
  wine: { label: 'Wines', icon: Wine, color: 'purple' },
  spirits: { label: 'Spirits & Liquors', icon: Martini, color: 'slate' },
  cocktails: { label: 'Cocktails', icon: Martini, color: 'pink' },
  coffee: { label: 'Coffee & Hot Drinks', icon: Coffee, color: 'orange' },
};

export default function DrinksCatalogManagement() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState({});
  const [activeCategory, setActiveCategory] = useState('soft');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Edit/Add dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    sizes: '',
    stockSizes: '',
    menuSizes: '',
    image: '',
    ingredients: ''
  });
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Image upload
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchGlobalCatalog();
  }, []);

  const fetchGlobalCatalog = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/drinks-catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.catalog) {
        setCatalog(res.data.catalog);
      }
    } catch (error) {
      console.error('Failed to fetch global catalog:', error);
      toast.error('Failed to load drinks catalog');
    } finally {
      setLoading(false);
    }
  };

  // Get icon component for category
  const getCategoryIcon = (categoryKey) => {
    return CATEGORIES[categoryKey]?.icon || GlassWater;
  };

  // Get color class for category
  const getCategoryColor = (categoryKey) => {
    const colors = {
      soft: 'from-blue-100 to-blue-200 text-blue-600',
      beer: 'from-amber-100 to-amber-200 text-amber-600',
      wine: 'from-purple-100 to-purple-200 text-purple-600',
      spirits: 'from-slate-100 to-slate-200 text-slate-600',
      cocktails: 'from-pink-100 to-pink-200 text-pink-600',
      coffee: 'from-orange-100 to-orange-200 text-orange-600',
    };
    return colors[categoryKey] || colors.soft;
  };

  // Filter items by search
  const getFilteredItems = (items) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(query) ||
      (item.category && item.category.toLowerCase().includes(query))
    );
  };

  // Open edit dialog
  const handleEditItem = (item, categoryKey) => {
    setEditingItem({ ...item, categoryKey });
    setIsNewItem(false);
    setEditForm({
      name: item.name || '',
      category: item.category || '',
      sizes: (item.sizes || []).join(', '),
      stockSizes: (item.stockSizes || []).join(', '),
      menuSizes: (item.menuSizes || []).join(', '),
      image: item.image || '',
      ingredients: item.ingredients || ''
    });
    setShowEditDialog(true);
  };

  // Open add new item dialog
  const handleAddNewItem = () => {
    setEditingItem({ categoryKey: activeCategory });
    setIsNewItem(true);
    setEditForm({
      name: '',
      category: CATEGORIES[activeCategory]?.label || '',
      sizes: '',
      stockSizes: '',
      menuSizes: '',
      image: '',
      ingredients: ''
    });
    setShowEditDialog(true);
  };

  // Save item
  const handleSaveItem = async () => {
    if (!editForm.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const itemData = {
        name: editForm.name.trim(),
        category: editForm.category.trim(),
        sizes: editForm.sizes.split(',').map(s => s.trim()).filter(Boolean),
        stockSizes: editForm.stockSizes.split(',').map(s => s.trim()).filter(Boolean),
        menuSizes: editForm.menuSizes.split(',').map(s => s.trim()).filter(Boolean),
        image: editForm.image.trim(),
        ingredients: editForm.ingredients.trim(),
        category_key: editingItem.categoryKey
      };

      if (isNewItem) {
        await axios.post(`${API_URL}/admin/drinks-catalog/items`, itemData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Drink added to global catalog');
      } else {
        await axios.put(`${API_URL}/admin/drinks-catalog/items/${encodeURIComponent(editingItem.name)}`, itemData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Drink updated in global catalog');
      }

      setShowEditDialog(false);
      setEditingItem(null);
      fetchGlobalCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save drink');
    } finally {
      setSaving(false);
    }
  };

  // Delete item
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    setSaving(true);
    try {
      await axios.delete(`${API_URL}/admin/drinks-catalog/items/${encodeURIComponent(itemToDelete.name)}?category_key=${itemToDelete.categoryKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Drink removed from global catalog');
      setShowDeleteDialog(false);
      setItemToDelete(null);
      fetchGlobalCatalog();
    } catch (error) {
      toast.error('Failed to delete drink');
    } finally {
      setSaving(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('drink_name', editForm.name || 'drink');

      const res = await axios.post(`${API_URL}/admin/drinks-catalog/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' 
        }
      });

      if (res.data?.image_url) {
        setEditForm(prev => ({ ...prev, image: res.data.image_url }));
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Get stats
  const getTotalItems = () => {
    return Object.values(catalog).reduce((acc, cat) => acc + (cat.items?.length || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Global Drinks Catalog</h1>
          <p className="text-slate-500">
            Manage the drinks catalog for all restaurants. Changes apply globally.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchGlobalCatalog}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleAddNewItem} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" />
            Add Drink
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{getTotalItems()}</p>
            <p className="text-xs text-orange-700">Total Items</p>
          </CardContent>
        </Card>
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          const count = catalog[key]?.items?.length || 0;
          return (
            <Card key={key} className={`bg-gradient-to-br ${getCategoryColor(key).split(' ')[0]} ${getCategoryColor(key).split(' ')[1]} border-${cat.color}-200`}>
              <CardContent className="p-4 text-center">
                <Icon className={`w-5 h-5 mx-auto mb-1 text-${cat.color}-600`} />
                <p className={`text-xl font-bold text-${cat.color}-600`}>{count}</p>
                <p className={`text-xs text-${cat.color}-700 truncate`}>{cat.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search drinks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="grid grid-cols-3 lg:grid-cols-6 mb-4">
          {Object.entries(CATEGORIES).map(([key, cat]) => {
            const Icon = cat.icon;
            return (
              <TabsTrigger key={key} value={key} className="flex items-center gap-1">
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{cat.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          const items = catalog[key]?.items || [];
          const filteredItems = getFilteredItems(items);

          return (
            <TabsContent key={key} value={key}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 text-${cat.color}-500`} />
                      <CardTitle>{cat.label}</CardTitle>
                      <Badge variant="secondary">{items.length} items</Badge>
                    </div>
                    <Button size="sm" onClick={handleAddNewItem}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add to {cat.label}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Icon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No drinks in this category</p>
                      <Button variant="link" onClick={handleAddNewItem}>
                        Add the first one
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredItems.map((item, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                        >
                          {/* Image */}
                          <div className="flex-shrink-0">
                            {item.image ? (
                              <img 
                                src={item.image} 
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover border"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getCategoryColor(key)} flex items-center justify-center border`}
                              style={{ display: item.image ? 'none' : 'flex' }}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">{item.name}</h4>
                            <p className="text-xs text-slate-500 truncate">{item.category}</p>
                            {item.ingredients && (
                              <p className="text-xs text-slate-400 truncate italic">{item.ingredients}</p>
                            )}
                          </div>

                          {/* Sizes */}
                          <div className="hidden md:flex flex-wrap gap-1 max-w-[200px]">
                            {(item.sizes || []).slice(0, 3).map((size, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{size}</Badge>
                            ))}
                            {(item.sizes || []).length > 3 && (
                              <Badge variant="outline" className="text-xs">+{item.sizes.length - 3}</Badge>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditItem(item, key)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setItemToDelete({ ...item, categoryKey: key });
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Edit/Add Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isNewItem ? <Plus className="w-5 h-5 text-green-500" /> : <Edit2 className="w-5 h-5 text-blue-500" />}
              {isNewItem ? 'Add New Drink' : 'Edit Drink'}
            </DialogTitle>
            <DialogDescription>
              {isNewItem 
                ? 'Add a new drink to the global catalog. All restaurants will see this.'
                : 'Edit this drink. Changes apply to all restaurants.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Image Upload */}
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                {editForm.image ? (
                  <img 
                    src={editForm.image}
                    alt="Preview"
                    className="w-20 h-20 rounded-lg object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-dashed border-slate-300">
                    <Image className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Image
                </Button>
                <Input
                  placeholder="Or paste image URL"
                  value={editForm.image}
                  onChange={(e) => setEditForm(prev => ({ ...prev, image: e.target.value }))}
                  className="text-xs"
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <Label>Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Coca-Cola"
              />
            </div>

            {/* Category */}
            <div>
              <Label>Sub-Category</Label>
              <Input
                value={editForm.category}
                onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g. Refrescos, Cervezas Premium"
              />
            </div>

            {/* Sizes */}
            <div>
              <Label>Default Sizes (comma separated)</Label>
              <Input
                value={editForm.sizes}
                onChange={(e) => setEditForm(prev => ({ ...prev, sizes: e.target.value }))}
                placeholder="e.g. 33cl Lata, 50cl Botella, 1L Botella"
              />
            </div>

            {/* Stock Sizes */}
            <div>
              <Label>Stock/Purchase Sizes (comma separated)</Label>
              <Input
                value={editForm.stockSizes}
                onChange={(e) => setEditForm(prev => ({ ...prev, stockSizes: e.target.value }))}
                placeholder="e.g. Pack 6, Pack 12, Caja 24"
              />
              <p className="text-xs text-slate-400 mt-1">Sizes shown when adding to inventory</p>
            </div>

            {/* Menu Sizes */}
            <div>
              <Label>Menu/Serving Sizes (comma separated)</Label>
              <Input
                value={editForm.menuSizes}
                onChange={(e) => setEditForm(prev => ({ ...prev, menuSizes: e.target.value }))}
                placeholder="e.g. Caña, Tubo, Jarra"
              />
              <p className="text-xs text-slate-400 mt-1">Sizes shown when adding to menu</p>
            </div>

            {/* Ingredients (for cocktails) */}
            <div>
              <Label>Ingredients/Description (optional)</Label>
              <Input
                value={editForm.ingredients}
                onChange={(e) => setEditForm(prev => ({ ...prev, ingredients: e.target.value }))}
                placeholder="e.g. Ron, lima, menta, azúcar, soda"
              />
            </div>

            {/* Category Selection */}
            <div>
              <Label>Category</Label>
              <Select
                value={editingItem?.categoryKey || activeCategory}
                onValueChange={(v) => setEditingItem(prev => ({ ...prev, categoryKey: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveItem}
              disabled={saving || !editForm.name.trim()}
              className={isNewItem ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isNewItem ? 'Add Drink' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Delete Drink?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{itemToDelete?.name}</strong> from the global catalog?
              This will remove it for all restaurants.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
